import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import errorHandler from '@/services/errorHandler';

export type ApiResponse = any;

/**
 * HTTP-клиент: interceptor возвращает response.data, не AxiosResponse.
 * Два generic как у axios: <T, R> — T тип тела запроса, R тип ответа (по умолчанию R = T).
 * Примеры: post<any, ApiResponse>(...), get<{ items: Item[] }>(...).
 */
export interface ApiHttpClient {
    get<T = ApiResponse, R = T>(url: string, config?: AxiosRequestConfig): Promise<R>;
    post<T = ApiResponse, R = T>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R>;
    put<T = ApiResponse, R = T>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R>;
    patch<T = ApiResponse, R = T>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R>;
    delete<T = ApiResponse, R = T>(url: string, config?: AxiosRequestConfig): Promise<R>;
    request<T = ApiResponse, R = T>(config: AxiosRequestConfig): Promise<R>;
    defaults: typeof axios.defaults;
    interceptors: ReturnType<typeof axios.create>['interceptors'];
}

interface ApiError {
    status: number
    body: any
    message?: string
    errors?: { [key: string]: string[] }
}

const instance = axios.create({
    baseURL: `${process.env.VUE_APP_API_ENDPOINT}/api`,
    // timeout: 10000,
    headers: {
        'accept': 'application/json',
    }
});

// Настройка интерсепторов
instance.interceptors.response.use(
    function (response: AxiosResponse<any,ApiResponse>): any {
        // Обработка успешного ответа
        return response.data;
    },
    function (error: AxiosError): Promise<never> {
        // Формируем объект ошибки в стандартном формате
        const apiError: ApiError = {
            status: error.response ? error.response.status : 500,
            body: error.response ? error.response.data : { message: 'Unknown error' }
        };

        // Обрабатываем через errorHandler (без показа диалога, только логирование)
        // errorHandler вернет человекопонятное сообщение, которое компонент может использовать
        // 
        // ВАЖНО: При 401 errorHandler автоматически:
        // - Удалит токен из localStorage.removeItem('access_token')
        // - Сделает редирект на /login
        // Это заменяет старую логику очистки токена
        const { displayMessage, fieldErrors } = errorHandler.handle(error as any, { 
            showDialog: false, // Компонент сам решит, показывать диалог или нет
            redirectOnAuth: true // Автоматический редирект при 401 с очисткой токена
        });

        // Добавляем человекопонятное сообщение в тело ответа для компонентов
        if (apiError.body && typeof apiError.body === 'object') {
            apiError.body._humanMessage = displayMessage;
            apiError.body._fieldErrors = fieldErrors;
        }

        // Возвращаем ошибку в том же формате, что и раньше (для совместимости)
        return Promise.reject(apiError);
    }
);

instance.interceptors.request.use(function (config) {
    // Do something before request is sent
    config.headers['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
    return config;
});

export default instance as ApiHttpClient;